/**
 * The staff "Courier service" list (order to invoice doc 104 §9.1, the
 * mock-up's tab "Delivery section"): every courier option the price preview
 * priced for this delivery — Service · Courier · Cost · Price, "Cheapest" on
 * the default, an unavailable one muted with the server's reason — filtered by
 * the search box, and a last "Agreed price" row revealing the agreed price
 * and its reason. Every word about an option is the server's.
 */
import { useId, useState } from "react";
import type {
  CatalogueDelivery,
  CatalogueDeliveryOption,
} from "@shared-core/data/catalogue-types";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { cn } from "../../primitives/utils";
import { Input } from "../../primitives/input";
import { Button } from "../../primitives/button";

/** Which row is chosen: the default (none yet), one option, or an agreed price. */
export type CourierChoice =
  | { kind: "default" }
  | {
      kind: "option";
      serviceLevel: CatalogueDeliveryOption["serviceLevel"];
      courierSupplierId: string | null;
    }
  | { kind: "agreed" };

const ROW =
  "grid grid-cols-[1.25rem_minmax(0,1fr)_5rem_4.5rem_4.5rem] sm:grid-cols-[1.25rem_minmax(0,1fr)_5rem_4.5rem_4.5rem_5.5rem] items-center gap-2.5 px-2.5 py-2";

function money(pence: number | null): string {
  return pence == null ? "—" : formatQuotedPrice(pence);
}

function isChosen(option: CatalogueDeliveryOption, choice: CourierChoice) {
  if (choice.kind === "agreed") return false;
  if (choice.kind === "default") return option.isDefault;
  return (
    option.serviceLevel === choice.serviceLevel &&
    option.courierSupplierId === choice.courierSupplierId
  );
}

export function CourierServiceList({
  delivery,
  choice,
  onChoose,
  agreedPrice,
  onAgreedPriceChange,
  agreedReason,
  onAgreedReasonChange,
  onAgreedBlur,
  choiceError = null,
  agreedPriceError,
  agreedReasonError,
  canClearAgreed = true,
  disabled = false,
}: {
  delivery: CatalogueDelivery;
  choice: CourierChoice;
  onChoose: (choice: CourierChoice) => void;
  /** The agreed price as typed, in pounds. */
  agreedPrice: string;
  onAgreedPriceChange: (value: string) => void;
  agreedReason: string;
  onAgreedReasonChange: (value: string) => void;
  /** Focus left the agreed price or its reason: the place to read the price again. */
  onAgreedBlur?: () => void;
  /** The server's refusal of the chosen level (`delivery.service_level`), under the list. */
  choiceError?: string | null;
  /** The server's refusals of the agreed price and its reason, beside their inputs. */
  agreedPriceError: string | null;
  agreedReasonError: string | null;
  /** False where the host has nothing to go back to (a delivery agreed on the item page, no courier to pick). */
  canClearAgreed?: boolean;
  disabled?: boolean;
}) {
  const id = useId();
  const [search, setSearch] = useState("");
  const needle = search.trim().toLowerCase();
  const options = needle
    ? delivery.options.filter((o) =>
        [o.serviceLevelLabel, o.courierName].some((text) =>
          text.toLowerCase().includes(needle),
        ),
      )
    : delivery.options;
  const isAgreed = choice.kind === "agreed";

  return (
    <div className="space-y-2">
      <label htmlFor={`${id}-search`} className="block text-sm font-medium">
        Courier service
      </label>
      <Input
        id={`${id}-search`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by service or courier…"
        disabled={disabled}
      />
      <div
        role="radiogroup"
        aria-label="Courier services"
        aria-describedby={choiceError ? `${id}-choice-error` : undefined}
        className="overflow-hidden rounded-md border border-border text-sm"
      >
        <div className={cn(ROW, "text-xs text-muted-foreground")} aria-hidden>
          <span />
          <span>Service</span>
          <span>Courier</span>
          <span className="text-right">Cost</span>
          <span className="text-right">Price</span>
          <span className="hidden sm:block" />
        </div>
        {options.map((o, i) => {
          const chosen = isChosen(o, choice);
          const rowId = `${id}-row-${i}`;
          // The radio is named by the service and the courier, and described by what the row says of them: the
          // cost, the price, and "Cheapest" or why it is not offered.
          const note = o.isAvailable
            ? o.isDefault
              ? "Cheapest"
              : ""
            : (o.unavailableReason ?? "");
          const describedBy = [
            o.carriageCostPence != null ? `${rowId}-cost` : null,
            o.deliveryPricePence != null ? `${rowId}-price` : null,
            note ? `${rowId}-note` : null,
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <label
              key={`${o.serviceLevel}:${o.courierSupplierId ?? o.courierName}`}
              className={cn(
                ROW,
                "border-t border-border",
                o.isAvailable ? "cursor-pointer" : "text-muted-foreground",
                chosen && "bg-primary/10",
              )}
            >
              <input
                type="radio"
                name={`${id}-courier`}
                checked={chosen}
                disabled={disabled || !o.isAvailable}
                onChange={() =>
                  onChoose({
                    kind: "option",
                    serviceLevel: o.serviceLevel,
                    courierSupplierId: o.courierSupplierId,
                  })
                }
                aria-invalid={choiceError ? true : undefined}
                aria-labelledby={`${rowId}-service ${rowId}-courier`}
                aria-describedby={describedBy || undefined}
              />
              <span className="min-w-0">
                <span id={`${rowId}-service`}>{o.serviceLevelLabel}</span>
                {!o.isAvailable && o.unavailableReason && (
                  <span className="block text-xs sm:hidden">
                    {o.unavailableReason}
                  </span>
                )}
              </span>
              <span id={`${rowId}-courier`}>{o.courierName}</span>
              <span id={`${rowId}-cost`} className="text-right tabular-nums">
                {money(o.carriageCostPence)}
              </span>
              <span
                id={`${rowId}-price`}
                className="text-right font-medium tabular-nums"
              >
                {money(o.deliveryPricePence)}
              </span>
              <span
                id={`${rowId}-note`}
                className="hidden text-xs text-muted-foreground sm:block"
              >
                {note}
              </span>
            </label>
          );
        })}
        <label
          className={cn(
            ROW,
            "cursor-pointer",
            options.length > 0 && "border-t border-border",
            isAgreed && "bg-primary/10",
          )}
        >
          <input
            type="radio"
            name={`${id}-courier`}
            checked={isAgreed}
            disabled={disabled}
            onChange={() => onChoose({ kind: "agreed" })}
          />
          <span>Agreed price</span>
        </label>
      </div>
      {choiceError && (
        <p id={`${id}-choice-error`} className="text-sm text-red-600">
          {choiceError}
        </p>
      )}
      {isAgreed && (
        <AgreedPriceFields
          agreedPrice={agreedPrice}
          onAgreedPriceChange={onAgreedPriceChange}
          agreedReason={agreedReason}
          onAgreedReasonChange={onAgreedReasonChange}
          onAgreedBlur={onAgreedBlur}
          // With no courier row to move to (none covers the address), clearing is the only way back off "Agreed price".
          onClear={
            canClearAgreed ? () => onChoose({ kind: "default" }) : undefined
          }
          agreedPriceError={agreedPriceError}
          agreedReasonError={agreedReasonError}
          disabled={disabled}
        />
      )}
    </div>
  );
}

/** "Agreed price" and its "Reason" (doc 104 §5.3): a price staff agree, no markup. Also how a route withheld because
 *  its delivery isn't priced becomes an option (doc 107). */
export function AgreedPriceFields({
  agreedPrice,
  onAgreedPriceChange,
  agreedReason,
  onAgreedReasonChange,
  onAgreedBlur,
  onClear,
  agreedPriceError,
  agreedReasonError,
  disabled = false,
}: {
  agreedPrice: string;
  onAgreedPriceChange: (value: string) => void;
  agreedReason: string;
  onAgreedReasonChange: (value: string) => void;
  onAgreedBlur?: () => void;
  /** Takes the agreed price off again (the host forgets the price and the reason, back to the default); none: nothing to take off. */
  onClear?: () => void;
  agreedPriceError: string | null;
  agreedReasonError: string | null;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
      <div className="space-y-1">
        <label htmlFor={`${id}-agreed`} className="block text-sm font-medium">
          Agreed price
        </label>
        <Input
          id={`${id}-agreed`}
          inputMode="decimal"
          placeholder="£"
          value={agreedPrice}
          onChange={(e) => onAgreedPriceChange(e.target.value)}
          onBlur={onAgreedBlur}
          disabled={disabled}
          className="tabular-nums"
          aria-invalid={agreedPriceError ? true : undefined}
          aria-describedby={agreedPriceError ? `${id}-agreed-error` : undefined}
        />
        {agreedPriceError && (
          <p id={`${id}-agreed-error`} className="text-sm text-red-600">
            {agreedPriceError}
          </p>
        )}
      </div>
      <div className="space-y-1">
        <label htmlFor={`${id}-reason`} className="block text-sm font-medium">
          Reason
        </label>
        <Input
          id={`${id}-reason`}
          placeholder="Why this price"
          value={agreedReason}
          onChange={(e) => onAgreedReasonChange(e.target.value)}
          onBlur={onAgreedBlur}
          disabled={disabled}
          // The schema's bound on a reason: past it the request is refused unformatted, so the input stops it.
          maxLength={200}
          aria-invalid={agreedReasonError ? true : undefined}
          aria-describedby={
            agreedReasonError ? `${id}-reason-error` : undefined
          }
        />
        {agreedReasonError && (
          <p id={`${id}-reason-error`} className="text-sm text-red-600">
            {agreedReasonError}
          </p>
        )}
      </div>
      {onClear && (
        <div className="sm:col-span-2">
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto px-0"
            onClick={onClear}
            disabled={disabled}
          >
            Clear agreed price
          </Button>
        </div>
      )}
    </div>
  );
}
